import React, { useState, useEffect } from 'react';
import axios from '../../api/axios';
import { Badge } from '../../components/ui';

const ServiceProviderHistory = ({ provider }) => {
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({
        start_date: '',
        end_date: '',
        status: '',
        unit_id: ''
    });

    const fetchHistory = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (filters.start_date) params.append('start_date', filters.start_date);
            if (filters.end_date) params.append('end_date', filters.end_date);
            if (filters.status) params.append('status', filters.status);
            if (filters.unit_id) params.append('unit_id', filters.unit_id);

            const res = await axios.get(`/api/v1/visitor-service-providers/${provider.id}/history?${params.toString()}`);
            setEntries(res.data.data.data || []);
        } catch (error) {
            console.error('Failed to fetch history', error);
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchHistory();
    }, [filters, provider.id]);

    const latestEntry = entries.length > 0 && !filters.status && !filters.start_date && !filters.end_date && !filters.unit_id ? entries[0] : null;

    return (
        <div className="space-y-6">
            {latestEntry && (
                <div className="bg-slate-50 dark:bg-slate-800/40 rounded-xl p-5 border border-slate-100 dark:border-slate-700/50">
                    <h4 className="font-semibold text-slate-800 dark:text-slate-200 mb-4 flex items-center gap-2">
                        <svg className="w-4 h-4 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                        Last Visit
                    </h4>
                    {!latestEntry.exit_time ? (
                        <div className="flex items-center gap-2 mb-3">
                            <span className="relative flex h-3 w-3">
                              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                              <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500"></span>
                            </span>
                            <span className="font-semibold text-emerald-600 dark:text-emerald-400">Currently Inside</span>
                        </div>
                    ) : null}
                    <div className="text-sm space-y-1.5 text-slate-700 dark:text-slate-300">
                        <p><span className="text-slate-500 dark:text-slate-400 w-20 inline-block">Unit:</span> <span className="font-medium">{latestEntry.unit?.block_name}-{latestEntry.unit?.flat_number}</span></p>
                        <p><span className="text-slate-500 dark:text-slate-400 w-20 inline-block">Check-in:</span> <span className="font-medium">{new Date(latestEntry.entry_time).toLocaleString()}</span></p>
                        {latestEntry.exit_time && (
                            <>
                                <p><span className="text-slate-500 dark:text-slate-400 w-20 inline-block">Check-out:</span> <span className="font-medium">{new Date(latestEntry.exit_time).toLocaleString()}</span></p>
                                <p><span className="text-slate-500 dark:text-slate-400 w-20 inline-block">Duration:</span> <span className="font-medium">{latestEntry.duration_formatted}</span></p>
                            </>
                        )}
                    </div>
                </div>
            )}

            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
                <div className="p-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 flex flex-wrap gap-3 items-center">
                    <h4 className="font-semibold text-slate-800 dark:text-slate-200 mr-auto">Activity History</h4>
                    <div className="flex gap-2 items-center">
                        <input 
                            type="date"
                            className="text-sm p-1.5 border rounded dark:bg-slate-800 dark:border-slate-700 focus:outline-none focus:border-primary"
                            value={filters.start_date}
                            onChange={e => setFilters({...filters, start_date: e.target.value})}
                        />
                        <span className="text-slate-400">-</span>
                        <input 
                            type="date"
                            className="text-sm p-1.5 border rounded dark:bg-slate-800 dark:border-slate-700 focus:outline-none focus:border-primary"
                            value={filters.end_date}
                            onChange={e => setFilters({...filters, end_date: e.target.value})}
                        />
                    </div>
                    <select 
                        className="text-sm p-1.5 border rounded dark:bg-slate-800 dark:border-slate-700 focus:outline-none focus:border-primary"
                        value={filters.status}
                        onChange={e => setFilters({...filters, status: e.target.value})}
                    >
                        <option value="">All Statuses</option>
                        <option value="inside">Currently Inside</option>
                        <option value="checked_out">Checked Out</option>
                    </select>
                    <select 
                        className="text-sm p-1.5 border rounded dark:bg-slate-800 dark:border-slate-700 focus:outline-none focus:border-primary"
                        value={filters.unit_id}
                        onChange={e => setFilters({...filters, unit_id: e.target.value})}
                    >
                        <option value="">All Units</option>
                        {provider.units?.map(u => (
                            <option key={u.unit_id} value={u.unit_id}>{u.unit?.block_name}-{u.unit?.flat_number}</option>
                        ))}
                    </select>
                </div>
                
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm text-slate-600 dark:text-slate-300">
                        <thead className="bg-slate-50 dark:bg-slate-800/80 text-xs uppercase font-semibold text-slate-500 dark:text-slate-400">
                            <tr>
                                <th className="px-4 py-3">Date</th>
                                <th className="px-4 py-3">Unit</th>
                                <th className="px-4 py-3">Check-in</th>
                                <th className="px-4 py-3">Check-out</th>
                                <th className="px-4 py-3">Duration</th>
                                <th className="px-4 py-3">Status</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-700/50">
                            {loading ? (
                                <tr>
                                    <td colSpan="6" className="px-4 py-8 text-center text-slate-400">Loading history...</td>
                                </tr>
                            ) : entries.length === 0 ? (
                                <tr>
                                    <td colSpan="6" className="px-4 py-8 text-center text-slate-400">No visits recorded for this provider.</td>
                                </tr>
                            ) : entries.map(entry => (
                                <tr key={entry.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                                    <td className="px-4 py-3 whitespace-nowrap">{new Date(entry.entry_time).toLocaleDateString()}</td>
                                    <td className="px-4 py-3 font-medium text-slate-700 dark:text-slate-200">{entry.unit?.block_name}-{entry.unit?.flat_number}</td>
                                    <td className="px-4 py-3 whitespace-nowrap">{new Date(entry.entry_time).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</td>
                                    <td className="px-4 py-3 whitespace-nowrap">{entry.exit_time ? new Date(entry.exit_time).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : '-'}</td>
                                    <td className="px-4 py-3 whitespace-nowrap">{entry.duration_formatted || '-'}</td>
                                    <td className="px-4 py-3">
                                        <Badge variant={!entry.exit_time ? 'success' : 'secondary'} className="text-[10px]">
                                            {!entry.exit_time ? 'Inside' : 'Checked Out'}
                                        </Badge>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default ServiceProviderHistory;
